import { FileLoader, FileRepository, Plugin, UploadAdapter, UploadResponse } from 'ckeditor5';

export class Base64ClampedSizeImageUploadAdapter extends Plugin {
  static MAX_WIDTH = 200;
  static MAX_HEIGHT = 200;

  static get requires() {
    return [FileRepository];
  }

  static get pluginName() {
    return 'Base64ClampedSizeImageUploadAdapter';
  }

  init() {
    this.editor.plugins.get(FileRepository).createUploadAdapter = loader => {
      return new Adapter(
        loader,
        Base64ClampedSizeImageUploadAdapter.MAX_WIDTH,
        Base64ClampedSizeImageUploadAdapter.MAX_HEIGHT
      );
    };
  }
}

class Adapter implements UploadAdapter {
  private reader?: FileReader;

  constructor(
    public loader: FileLoader,
    public max_width = 200,
    public max_height = 200
  ) {}

  private getClampedImageSize(imgWidth: number, imgHeight: number) {
    const aspectRatio = imgWidth / imgHeight;

    let resultWidth = Math.min(this.max_width, imgWidth);
    let resultHeight = Math.min(this.max_height, imgHeight);

    if (aspectRatio > 1) {
      resultHeight = (resultWidth * imgHeight) / imgWidth;
    } else {
      resultWidth = (resultHeight * imgWidth) / imgHeight;
    }

    return { width: resultWidth, height: resultHeight };
  }

  upload(): Promise<UploadResponse> {
    return new Promise((resolve, reject) => {
      const reader = (this.reader = new window.FileReader());

      reader.onerror = err => reject(err);
      reader.onabort = () => reject();

      reader.onload = event => {
        const image = new Image();

        image.onload = () => {
          const { width, height } = this.getClampedImageSize(image.width, image.height);

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;

          canvas.getContext('2d')?.drawImage(image, 0, 0, width, height);
          const dataUrl = canvas.toDataURL('image/png');

          resolve({ default: dataUrl });
        };

        if (event.target?.result) {
          image.src = event.target.result as string;
        } else {
          reject();
        }
      };

      this.loader.file.then(file => {
        if (file) {
          reader.readAsDataURL(file);
        } else {
          reject();
        }
      });
    });
  }

  abort(): void {
    this.reader?.abort();
  }
}
